/* eslint-disable max-lines */
import { ColorTag, FilterTagWrapper, Tags } from "@/components/tags";
import { useAppState } from "@/modules/app/useAppContext";
import { useNavigate, useParams } from "react-router-dom";
import { Paginate } from "@/components/paginate";
import { getQueryFilters, getAllQueries } from "@/helpers/apis";
import { RelativeComponentLoader } from "@/components/loader";
import { useEffect } from "react";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { useQuery } from "@tanstack/react-query";
import { GET_QUERY } from "../query/constants";
import InsightLabel from "../query/InsightLabel";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { FilterApplicableFor } from "@/components/tags/types";
import SavedFilters from "@/components/tags/SavedFilters";
import classNames from "classnames";
import QueryTextWithHover from "../query/QueryText";
import QueryHashWithHover from "../query/QueryHash";
import { formatNumber } from "@/uiCore";
import { Stack } from "@altimateai/lego";
import { colors } from "../query/QueryConstants";
import { DataTable } from "@/components/tables";
import { stringToNumber } from "@/helpers/utils";
import usePaginate from "@/components/paginate/usePaginate";
import { DownloadTable } from "@/components/downloadTable";
import { downloadAllQueryCSV } from "@/helpers/apis";
import { toast } from "@altimateai/lego";
import { FilterEntityType } from "@/pages/query/types";
import { FilterType, createAPIDropdownFilter, parseSortDirection } from "@/components/tables/types";
import { FilterTextChangeValue } from "@/components/filterDropdown";

export const WarehouseQueries: React.FC<{
  startDate: Date;
  endDate: Date;
  warehouse: string;
}> = ({ startDate, endDate, warehouse }) => {
  const { instance_id } = useParams();
  const { currency } = useAppState();
  const { searchParams, deleteSearchParamsByKeyValue, updateSearchParamsByKey } =
    useFiltersBySearchParams();
  const { currencySymbol } = useAppState();
  const tagFilter = searchParams.getAll("tags") || [];
  const executionTimeFilter = searchParams.get("executionTime");
  const queryCostFilter = searchParams.get("queryCost");
  const userFilter = searchParams.getAll("users") || [];
  const queryTypeFilter = searchParams.getAll("queryType") || [];

  const sortAttribute = searchParams.get("sortAttribute") || "query_cost";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"), "desc");

  const executionTimeFilterMode = searchParams.get("executionTimeFilterMode") || "gt";
  const executionCostFilterMode = searchParams.get("executionCostFilterMode") || "gt";

  const { page, pageSize, setPage, updatePageSize, resetPage } = usePaginate();
  const navigate = useNavigate();

  const getFilterParams = () => {
    const params: Record<string, string | number | string[]> = {
      page: page + 1,
      size: pageSize,
      warehouses: [warehouse],
    };
    if (tagFilter.length > 0) params.tags = tagFilter;
    if (userFilter.length > 0) params.users = userFilter;
    if (queryTypeFilter.length > 0) params.query_type = queryTypeFilter;
    if (sortAttribute) {
      params.sortAttribute = sortAttribute;
      params.sortOrder = sortOrder;
    }
    if (executionTimeFilter) {
      params.executionTimeFilter = parseFloat(executionTimeFilter);
      params.executionTimeFilterMode = executionTimeFilterMode === "lt" ? "lt" : "gt";
    }
    if (queryCostFilter) {
      params.executionCostFilter = queryCostFilter;
      params.executionCostFilterMode = executionCostFilterMode === "lt" ? "lt" : "gt";
    }
    if (instance_id) params.instance_ids = [instance_id];
    params.start_date = getDateByStartOfDayWithoutTimeoffset(startDate).toISOString();
    params.end_date = getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString()!;
    return params;
  };

  const {
    data: queries = { total: 0, items: [], pages: 0 },
    error: queryError,
    isLoading: isQueriesLoading,
  } = useQuery({
    queryKey: [
      GET_QUERY,
      page,
      pageSize,
      ...tagFilter,
      ...userFilter,
      ...queryTypeFilter,
      sortOrder,
      sortAttribute,
      executionTimeFilter,
      queryCostFilter,
      startDate,
      endDate,
      executionTimeFilterMode,
      executionCostFilterMode,
    ],
    queryFn: () => getAllQueries(getFilterParams()),
  });

  useEffect(() => {
    if (queryError) {
      toast({
        title: "Failed to fetch queries",
        description: `${(queryError as Error)?.message || queryError} Please try again.`,
        variant: "destructive",
      });
    }
  }, [queryError]);

  const handleDownloadClick = async () => {
    const params = getFilterParams();
    await downloadAllQueryCSV(params);
  };

  const handleCostFilterChange = ({ value, mode }: FilterTextChangeValue) => {
    updateSearchParamsByKey({
      queryCost: value,
      executionCostFilterMode: mode,
    });
  };

  const handleExecutionTimeFilterChange = ({ value, mode }: FilterTextChangeValue) => {
    updateSearchParamsByKey({
      executionTime: value,
      executionTimeFilterMode: mode,
    });
  };

  const handleUserFilterChange = (value: string[]) => {
    updateSearchParamsByKey({ users: value });
  };

  const handleInsightsFilterChange = (value: string[]) => {
    updateSearchParamsByKey({ tags: value });
  };

  const handleQueryTypeFilterChange = (value: string[]) => {
    updateSearchParamsByKey({ queryType: value });
  };

  if (isQueriesLoading) {
    return <RelativeComponentLoader label="Loading queries..." />;
  }

  return (
    <div>
      <Stack className="al-mb-2" wrap>
        <FilterTagWrapper
          applicableFor={FilterApplicableFor.WAREHOUSE_QUERIES}
          filters={{
            tagFilter: {
              filterStr: tagFilter,
              label: "Tags",
              searchKey: "tags",
              onclose: (removedFilter?: string) => {
                deleteSearchParamsByKeyValue({ tags: removedFilter });
              },
            },
            userFilter: {
              filterStr: userFilter,
              label: "Users",
              searchKey: "users",
              onclose: (removedFilter?: string) => {
                deleteSearchParamsByKeyValue({ users: removedFilter });
              },
            },
            queryTypeFilter: {
              filterStr: queryTypeFilter,
              label: "Query Type",
              searchKey: "queryType",
              onclose: (removedFilter?: string) => {
                deleteSearchParamsByKeyValue({ queryType: removedFilter });
              },
            },
            executionTimeFilter: {
              filterStr:
                (executionTimeFilter &&
                  `${executionTimeFilterMode === "lt" ? "<" : ">"} ${executionTimeFilter}`) ||
                "",
              label: "mins",
              filterData: executionTimeFilter
                ? {
                    executionTime: executionTimeFilter,
                    executionTimeFilterMode,
                  }
                : undefined,
              onclose: () => {
                deleteSearchParamsByKeyValue({
                  executionTime: undefined,
                  executionTimeFilterMode: undefined,
                });
              },
            },
            queryCostFilter: {
              filterStr:
                (queryCostFilter &&
                  `${executionCostFilterMode === "lt" ? "<" : ">"} ${queryCostFilter}`) ||
                "",
              filterData: queryCostFilter
                ? { queryCost: queryCostFilter, executionCostFilterMode }
                : undefined,
              label: currencySymbol,
              onclose: () => {
                deleteSearchParamsByKeyValue({
                  queryCost: undefined,
                  executionCostFilterMode: undefined,
                });
              },
            },
          }}
        />
        <div className="spacer" />
        <DownloadTable handleDownload={handleDownloadClick} />
        <SavedFilters applicableFor={FilterApplicableFor.WAREHOUSE_QUERIES} />
        <div id="columnpicker-container" />
      </Stack>
      <DataTable
        header={[
          {
            id: 1,
            uid: "query_text",
            label: "Query Text",
            width: "18%",
            render: item => (
              <div
                className={classNames("al-break-words", "text-primary al-break-all cursor-pointer")}
              >
                <QueryTextWithHover
                  queryText={item.query_trimmed as string}
                  queryHash={item.query_hash as string}
                />
              </div>
            ),
          },
          {
            id: 2,
            uid: "query_hash",
            label: "Query Hash",
            width: "18%",
            render: item => <QueryHashWithHover queryHash={item.query_hash as string} />,
          },
          {
            id: 3,
            uid: "est_cost",
            label: "Est. Cost",
            width: "7%",
            filter: {
              filterType: FilterType.TEXT,
              value: queryCostFilter || undefined,
              placeHolder: `Specify cost in ${currencySymbol}`,
              onChange: handleCostFilterChange,
              label: "Estimate Cost",
              filterMode: { value: executionCostFilterMode },
              filterModeSearchKey: "executionCostFilterMode",
            },
            sort: {
              sortSearchKey: "sortAttribute",
              sortParamValue: "query_cost",
            },
            render: item => (
              <div className="text-break">
                {item.query_cost != null ? formatNumber(item.query_cost, { currency }) : "N/A"}
              </div>
            ),
          },
          {
            id: 4,
            uid: "exec_time",
            label: "Exec. Time",
            width: "7%",
            filter: {
              filterType: FilterType.TEXT,
              value: executionTimeFilter || undefined,
              placeHolder: "Specify time in mins",
              onChange: handleExecutionTimeFilterChange,
              label: "Execution Time",
              filterMode: { value: executionTimeFilterMode },
              filterModeSearchKey: "executionTimeFilterMode",
              allowFractional: true,
            },
            sort: {
              sortSearchKey: "sortAttribute",
              sortParamValue: "execution_time",
            },
            render: item => <div className="text-break">{item.execution_time as string}</div>,
          },
          {
            id: 5,
            uid: "insights",
            label: "Insights",
            width: "18%",
            filter: createAPIDropdownFilter({
              filterType: FilterType.API_DROPDOWN,
              searchKey: "tags",
              value: tagFilter,
              apiFunction: ({ prefix, page }) =>
                getQueryFilters({ entity_type: FilterEntityType.TAGS, prefix, page }),
              seeMore: true,
              onChange: handleInsightsFilterChange,
              extractorFunction: data => {
                return (
                  data?.tags?.map(t => ({
                    label: <InsightLabel tag={t} />,
                    value: t.name,
                    showTooltip: false,
                  })) || []
                );
              },
            }),
            render: item => (
              <Tags>
                {(item?.insights as string[])?.map((tag_rk: string) => (
                  <ColorTag
                    key={tag_rk}
                    color={colors[stringToNumber(tag_rk, colors.length)]}
                    label={tag_rk}
                  />
                ))}
              </Tags>
            ),
          },
          {
            id: 6,
            uid: "timestamp",
            label: "Timestamp",
            width: "9%",
            sort: {
              sortSearchKey: "sortAttribute",
              sortParamValue: "end_time",
            },
            render: item => <div>{item.end_time as string}</div>,
          },
          {
            id: 7,
            uid: "query_type",
            label: "Query Type",
            width: "9%",
            filter: createAPIDropdownFilter({
              filterType: FilterType.API_DROPDOWN,
              searchKey: "queryType",
              value: queryTypeFilter,
              apiFunction: ({ prefix, page }) =>
                getQueryFilters({ entity_type: FilterEntityType.QUERY_TYPES, prefix, page }),
              seeMore: true,
              onChange: handleQueryTypeFilterChange,
              extractorFunction: data => {
                return (
                  data?.query_types?.map(qt => ({
                    label: qt,
                    value: qt,
                  })) || []
                );
              },
            }),
            render: item => <div className="text-break">{item.query_type as string}</div>,
          },
          {
            id: 8,
            uid: "user",
            label: "User",
            width: "14%",
            filter: createAPIDropdownFilter({
              filterType: FilterType.API_DROPDOWN,
              searchKey: "users",
              value: userFilter,
              apiFunction: ({ prefix, page }) =>
                getQueryFilters({ entity_type: FilterEntityType.USERS, prefix, page }),
              seeMore: true,
              onChange: handleUserFilterChange,
              extractorFunction: data => {
                return (
                  data?.users?.map(w => ({
                    label: w,
                    value: w,
                  })) || []
                );
              },
            }),
            render: item => <div className="text-break">{item.user_name as string}</div>,
          },
        ]}
        data={queries}
        onRowClick={({ query_id: v }) =>
          navigate(
            `/query/${v}${startDate ? "?start_date=" + startDate : ""}${
              endDate ? "&end_date=" + endDate : ""
            }`
          )
        }
        resetPage={resetPage}
        applicableFor={FilterApplicableFor.WAREHOUSE_QUERIES}
        columnPickerPortal="columnpicker-container"
        minWidth={1500}
      />
      <Paginate
        itemCount={queries.total}
        page={page}
        pageSize={pageSize}
        numPages={queries.pages}
        onPageClick={setPage}
        onPageSizeChange={updatePageSize}
        showPageSize={true}
      />
    </div>
  );
};
